import {
  createSlice,
  PayloadAction,
  createAsyncThunk,
  createSelector,
  isAnyOf,
} from '@reduxjs/toolkit';
import { AbortController } from '@azure/abort-controller';
import { RootState } from '../store';
import {
  ACCOUNTS_PAYABLE,
  ACCOUNTS_RECEIVABLES,
  INVENTORY,
  IUploadingFilesData,
  IUploadedFile,
  TFileSection,
  UPLOADED,
  DELETED,
  MAPPING,
  STAGED,
  TFileStatus,
  TPublishStatus,
  SECTIONS_NAME_MAP,
  IOtherFilesBySection,
  IUserFilesBySection,
  SESSION_CREATED,
  SESSION_UPLOADED,
  SESSION_TRANSFORMATION_DONE,
  DOWNLOADING,
  SESSION_DELETED,
  SECTIONS_MAP,
} from '../../types';
import {
  uploadFilesToCp,
  uploadFileToFxr,
  updateSessionState,
  fetchFiles,
  createFileUploadSession,
  getSessionStatuses,
  getFileUrls,
  getSchema,
  deleteAllData,
  deleteSessions,
} from '../../utils/api';
import moment from 'moment';
import { later } from '../../utils/later';
import { uniq } from 'lodash';

interface IFilesState {
  selectedSection: TFileSection;
  uploadingFilesCount: number;
  uploadingFilesData: IUploadingFilesData | null;
  isUploading: boolean;
  isFetching: boolean;
  isDeleting: boolean;
  otherFiles: IOtherFilesBySection;
  userFiles: IUserFilesBySection;
  allSessionIds: string[];
  schemas: { [key: string]: string | null };
  malformedRowsUrlMap: { [key: string]: string };
  sessionExpirationDatesMap: { [key: string]: string };
  error: string | null;
  publishStatus: TPublishStatus;
  instanceName: string | null;
}

interface IStatusesMap {
  section: TFileSection;
  sessionId: string;
  currentStatus: TFileStatus;
  newStatus: TFileStatus;
}

const TOKEN_ERROR = 'Token Error';
const AUTH_ERROR_MESSAGE =
  'You are not authorized to File X-Ray and you are not allowed to upload files. Please contact CCSI at IT-CCSI-Request@alixpartners.com';

const initialState: IFilesState = {
  selectedSection: ACCOUNTS_PAYABLE,
  uploadingFilesCount: 0,
  uploadingFilesData: null,
  isUploading: false,
  isFetching: false,
  isDeleting: false,
  publishStatus: 'None' as TPublishStatus,
  allSessionIds: [],
  otherFiles: {
    [ACCOUNTS_PAYABLE]: [],
    [ACCOUNTS_RECEIVABLES]: [],
    [INVENTORY]: [],
  },
  userFiles: {
    [ACCOUNTS_PAYABLE]: {
      [UPLOADED]: {},
      [DELETED]: {},
      [MAPPING]: {},
      [STAGED]: {},
    },
    [ACCOUNTS_RECEIVABLES]: {
      [UPLOADED]: {},
      [DELETED]: {},
      [MAPPING]: {},
      [STAGED]: {},
    },
    [INVENTORY]: {
      [UPLOADED]: {},
      [DELETED]: {},
      [MAPPING]: {},
      [STAGED]: {},
    },
  },
  schemas: {
    [ACCOUNTS_PAYABLE]: null,
    [ACCOUNTS_RECEIVABLES]: null,
    [INVENTORY]: null,
  },
  malformedRowsUrlMap: {},
  sessionExpirationDatesMap: {},
  error: null,
  instanceName: null,
};

const REACT_APP_PROJECT_CODE_NAME = process.env.REACT_APP_PROJECT_CODE_NAME;

const getTag = (instanceName: string | null, schemaName: string): string => {
  return `${instanceName}/${schemaName}`;
};

export const uploadFiles = createAsyncThunk(
  'files/uploadFiles',
  async (files: File[], { dispatch, getState, signal, rejectWithValue }) => {
    const controller = new AbortController();
    const abortSignal = controller.signal;
    const { uploadingFilesData, selectedSection, schemas } = (
      getState() as RootState
    ).files;

    dispatch(filesSlice.actions.setUploadingFilesCount(files.length));
    signal.addEventListener('abort', () => {
      controller.abort();
    });

    let uploadUrl = '';
    let sessionId = '';

    try {
      const schemaName: string = SECTIONS_MAP[selectedSection];
      const instanceName: string = REACT_APP_PROJECT_CODE_NAME || '';
      const tag: string = getTag(instanceName, schemaName);
      const result = await createFileUploadSession(
        schemas[selectedSection]!,
        tag
      );

      if (result) {
        uploadUrl = result.uploadUrl;
        sessionId = result.sessionId;
      }
    } catch (error: any) {
      if (error?.errorType && error.errorType === 'tokenError') {
        return rejectWithValue(TOKEN_ERROR);
      }
    }

    const filesToUpload: Array<Omit<IUploadedFile, 'id' | 'uploadDate'>> =
      files.map((f) => {
        const file: Omit<IUploadedFile, 'id' | 'uploadDate'> = {
          name: f.name,
          section: selectedSection,
          status: UPLOADED,
          sessionId,
          userName: uploadingFilesData!.userName,
          isMine: true,
        };

        return file;
      });

    try {
      await Promise.all(
        files.map((uploadedFile: File) =>
          uploadFileToFxr(uploadUrl, uploadedFile, abortSignal)
        )
      );
      await uploadFilesToCp(filesToUpload);
      dispatch(refetchAllFiles());
    } catch (error: any) {
      if (error.name === 'AbortError') {
        rejectWithValue('AbortError');
      } else {
        console.error('Error');
      }
    }
  }
);

export const fetchAllFiles = createAsyncThunk(
  'files/fetchAllFiles',
  async (_0: void, { dispatch }) => {
    const allFiles = await Promise.all([
      fetchFiles(ACCOUNTS_PAYABLE),
      fetchFiles(ACCOUNTS_RECEIVABLES),
      fetchFiles(INVENTORY),
    ]);

    dispatch(fetchSessionsStatus());

    return allFiles;
  }
);

export const refetchAllFiles = createAsyncThunk(
  'files/refetchAllFiles',
  async () => {
    return await Promise.all([
      fetchFiles(ACCOUNTS_PAYABLE),
      fetchFiles(ACCOUNTS_RECEIVABLES),
      fetchFiles(INVENTORY),
    ]);
  }
);

export const deleteAllFiles = createAsyncThunk(
  'files/deleteAllFiles',
  async (_, { dispatch, getState }) => {
    const { allSessionIds } = (getState() as RootState).files;

    try {
      await deleteAllData();

      if (allSessionIds.length > 0) {
        await deleteSessions(allSessionIds);
      }
    } finally {
      dispatch(refetchAllFiles());
    }
  }
);

export const fetchSchemas = createAsyncThunk('files/fetchSchemas', async () => {
  return await Promise.all([
    getSchema(ACCOUNTS_PAYABLE),
    getSchema(ACCOUNTS_RECEIVABLES),
    getSchema(INVENTORY),
  ]);
});

type Awaited<T> = T extends PromiseLike<infer U> ? U : T;
type SessionStatuses = Awaited<ReturnType<typeof getSessionStatuses>>;

export const fetchSessionsStatus = createAsyncThunk(
  'files/fetchSessionStatuses',
  async (_: void, { getState, dispatch, rejectWithValue }) => {
    // allow sunc action to be handled and update files state
    await later(1);

    const { userFiles } = (getState() as RootState).files;

    // Get sessions only for Mapping/Uploaded states
    const sessionIdList = [
      ...Object.keys(userFiles[ACCOUNTS_PAYABLE][MAPPING]),
      ...Object.keys(userFiles[ACCOUNTS_PAYABLE][UPLOADED]),
      ...Object.keys(userFiles[ACCOUNTS_RECEIVABLES][MAPPING]),
      ...Object.keys(userFiles[ACCOUNTS_RECEIVABLES][UPLOADED]),
      ...Object.keys(userFiles[INVENTORY][MAPPING]),
      ...Object.keys(userFiles[INVENTORY][UPLOADED]),
    ].map((s) => s);

    let formattedStatuses: IStatusesMap[] = [];

    if (sessionIdList.length > 0) {
      try {
        const statuses = await getSessionStatuses(sessionIdList);

        dispatch(filesSlice.actions.setExpirationDate(statuses));

        formattedStatuses = normalizeNewSessionStatuses(statuses, userFiles);

        if (formattedStatuses.length > 0) {
          dispatch(
            updateState(
              formattedStatuses.map((s) => ({
                sessionId: s.sessionId,
                status: s.newStatus,
              }))
            )
          );
        }
      } catch (error: any) {
        if (error?.errorType && error.errorType === 'tokenError') {
          return rejectWithValue(TOKEN_ERROR);
        }
      }
    }

    dispatch(fetcMalformedRows());

    return formattedStatuses;
  }
);

export const fetcMalformedRows = createAsyncThunk(
  'files/fetchSessionStatuses',
  async (_: void, { getState }) => {
    // allow sunc action to be handled and update files state
    await later(1);

    const { userFiles } = (getState() as RootState).files;

    const stagedSessionIdList = [
      ...Object.keys(userFiles[ACCOUNTS_PAYABLE][STAGED]),
      ...Object.keys(userFiles[ACCOUNTS_RECEIVABLES][STAGED]),
      ...Object.keys(userFiles[INVENTORY][STAGED]),
    ].map((s) => s);

    if (stagedSessionIdList.length > 0) {
      const stagedStatuses = await getSessionStatuses(stagedSessionIdList);

      return stagedStatuses.reduce(
        (map, { sessionId, malformedRowsUrl }) =>
          malformedRowsUrl
            ? {
                ...map,
                [sessionId]: malformedRowsUrl,
              }
            : map,
        {}
      );
    }

    return null;
  }
);

export const updateState = createAsyncThunk(
  'files/updateState',
  async (
    statusList: Array<{ sessionId: string; status: TFileStatus }>,
    { dispatch }
  ) => {
    for (const { sessionId, status } of statusList) {
      if (status === STAGED) {
        const response = await getFileUrls(sessionId);
        const params = {
          fileUrls: response.fileUrls,
          rawFileUrls: response.rawFileUrls,
          status,
        };
        await updateSessionState(sessionId, params);
      } else {
        await updateSessionState(sessionId, { status });
      }
    }

    dispatch(refetchAllFiles());
  }
);

export const filesSlice = createSlice({
  name: 'files',
  initialState,
  reducers: {
    setUploadingFilesCount: (state, action: PayloadAction<number>) => {
      state.uploadingFilesCount = action.payload;
    },
    setUploadingFilesData: (
      state,
      action: PayloadAction<IUploadingFilesData>
    ) => {
      state.uploadingFilesData = action.payload;
    },
    cancelUpload: (state) => {
      state.uploadingFilesCount = 0;
      state.isUploading = false;
    },
    setSelectedSection: (state, action: PayloadAction<TFileSection>) => {
      state.selectedSection = action.payload;
    },
    setPublishStatus: (state, action: PayloadAction<TPublishStatus>) => {
      state.publishStatus = action.payload;
    },
    setExpirationDate: (state, action: PayloadAction<SessionStatuses>) => {
      state.sessionExpirationDatesMap = action.payload.reduce(
        (acc, { sessionId, expirationDate = '' }) => ({
          ...acc,
          [sessionId]: expirationDate,
        }),
        {}
      );
    },
  },
  extraReducers: (builder) => {
    builder
      // uploadFiles request
      .addCase(uploadFiles.pending, (state) => {
        state.error = null;
        state.isUploading = true;
      })
      .addCase(uploadFiles.fulfilled, (state, action) => {
        state.isUploading = false;
        state.uploadingFilesCount = 0;
        state.uploadingFilesData = null;
      })
      .addCase(uploadFiles.rejected, (state, action) => {
        if (action.payload === TOKEN_ERROR) {
          state.error = AUTH_ERROR_MESSAGE;
        } else {
          state.error = null;
        }

        state.isUploading = false;
        state.uploadingFilesCount = 0;
        state.uploadingFilesData = null;
      })
      .addCase(fetcMalformedRows.fulfilled, (state, action) => {
        if (action.payload && Object.keys(action.payload).length > 0) {
          state.malformedRowsUrlMap = {
            ...state.malformedRowsUrlMap,
            ...action.payload,
          };
        }
      })
      .addCase(fetchSchemas.fulfilled, (state, action) => {
        const [
          accountsPayableSchema,
          accountsReceivablesSchema,
          inventorySchema,
        ] = action.payload;

        state.schemas[ACCOUNTS_PAYABLE] = JSON.stringify(accountsPayableSchema);
        state.schemas[ACCOUNTS_RECEIVABLES] = JSON.stringify(
          accountsReceivablesSchema
        );
        state.schemas[INVENTORY] = JSON.stringify(inventorySchema);
      })
      // fetchAllFiles request
      .addCase(fetchAllFiles.pending, (state) => {
        state.isFetching = true;
      })
      .addCase(fetchAllFiles.rejected, (state) => {
        state.isFetching = false;
      })
      // deleteAllFiles request
      .addCase(deleteAllFiles.pending, (state) => {
        state.isDeleting = true;
      })
      .addCase(deleteAllFiles.rejected, (state) => {
        state.isDeleting = false;
      })
      .addCase(deleteAllFiles.fulfilled, (state) => {
        state.isDeleting = false;
      })
      // fetchSessionsStatus request
      .addCase(fetchSessionsStatus.rejected, (state, action) => {
        if (action.payload === TOKEN_ERROR) {
          state.error = AUTH_ERROR_MESSAGE;
        } else {
          state.error = null;
        }
      })
      .addMatcher(
        isAnyOf(fetchAllFiles.fulfilled, refetchAllFiles.fulfilled),
        (state, action) => {
          const [
            accountsPayableFiles,
            accountsReceivablesFiles,
            inventoryFiles,
          ] = action.payload;

          state.isFetching = false;

          if (
            (accountsPayableFiles &&
              Array.isArray(accountsPayableFiles) &&
              accountsPayableFiles.length > 0) ||
            (accountsReceivablesFiles &&
              Array.isArray(accountsReceivablesFiles) &&
              accountsReceivablesFiles.length > 0) ||
            (inventoryFiles &&
              Array.isArray(inventoryFiles) &&
              inventoryFiles.length > 0)
          ) {
            const allFiles = [
              ...accountsPayableFiles,
              ...accountsReceivablesFiles,
              ...inventoryFiles,
            ];
            const { userFiles, otherFiles } = normalizeFileList(allFiles);
            state.userFiles = userFiles;
            state.otherFiles = otherFiles;
            state.allSessionIds = uniq(allFiles.map((file) => file.sessionId));
          } else {
            state.otherFiles = initialState.otherFiles;
            state.userFiles = initialState.userFiles;
            state.allSessionIds = initialState.allSessionIds;
          }
        }
      );
  },
});

const normalizeFileList = (files: IUploadedFile[]) => {
  const { otherFiles, userFiles } = files
    .sort((file1, file2) => {
      return moment(file2.uploadDate).diff(file1.uploadDate);
    })
    .reduce(
      (
        acc: { userFiles: IUploadedFile[]; otherFiles: IUploadedFile[] },
        current
      ) => {
        const file: IUploadedFile = {
          ...current,
          section: SECTIONS_NAME_MAP[current.section as string],
        };

        if (file.isMine) {
          acc.userFiles.push(file);
        } else {
          acc.otherFiles.push(file);
        }

        return acc;
      },
      { userFiles: [], otherFiles: [] }
    );

  return {
    otherFiles: otherFiles.reduce(
      (acc: IOtherFilesBySection, current) => {
        const section: TFileSection = current.section;

        acc[section].push(current);

        return acc;
      },
      {
        [ACCOUNTS_PAYABLE]: [],
        [ACCOUNTS_RECEIVABLES]: [],
        [INVENTORY]: [],
      }
    ),
    userFiles: userFiles.reduce(
      (acc: IUserFilesBySection, current) => {
        const section: TFileSection = current.section;
        let status: TFileStatus = current.status;
        const sessionId = current.sessionId;

        // We keep Downloading files in Mapping section
        if (status === DOWNLOADING) {
          status = MAPPING;
        }

        if (Object.keys(acc[section][status]).includes(sessionId)) {
          acc[section][status][sessionId].push(current);
        } else {
          acc[section][status][sessionId] = [current];
        }

        return acc;
      },
      {
        [ACCOUNTS_PAYABLE]: {
          [UPLOADED]: {},
          [DELETED]: {},
          [MAPPING]: {},
          [STAGED]: {},
        },
        [ACCOUNTS_RECEIVABLES]: {
          [UPLOADED]: {},
          [DELETED]: {},
          [MAPPING]: {},
          [STAGED]: {},
        },
        [INVENTORY]: {
          [UPLOADED]: {},
          [DELETED]: {},
          [MAPPING]: {},
          [STAGED]: {},
        },
      }
    ),
  };
};

const translateFXRStatus = (fxrStatus: string) =>
  [SESSION_CREATED, SESSION_UPLOADED].includes(fxrStatus)
    ? UPLOADED
    : fxrStatus === SESSION_DELETED
    ? DELETED
    : fxrStatus === SESSION_TRANSFORMATION_DONE
    ? STAGED
    : MAPPING;

const normalizeNewSessionStatuses = (
  statuses: Array<{ sessionId: string; status: string }>,
  userFiles: IUserFilesBySection
) => {
  const bySection = userFiles;

  return Object.keys(bySection).reduce((acc: IStatusesMap[], section) => {
    const byStatus = userFiles[section];

    Object.keys(byStatus).forEach((status) => {
      if ([UPLOADED, MAPPING].includes(status as TFileStatus)) {
        statuses.forEach(({ sessionId, status: fxrStatus }) => {
          if (Object.hasOwn(byStatus[status], sessionId)) {
            const bySession = userFiles[section][status];
            let currentStatus = bySession[sessionId][0].status;

            // We keep Downloading files in Mapping section
            if (currentStatus === DOWNLOADING) {
              currentStatus = STAGED;
            }
            const newStatus = translateFXRStatus(fxrStatus);

            if (newStatus !== currentStatus) {
              acc.push({
                section: section as TFileSection,
                sessionId,
                currentStatus,
                newStatus,
              });
            }
          }
        });
      }
    });
    return acc;
  }, []);
};

export const {
  setUploadingFilesData,
  cancelUpload,
  setSelectedSection,
  setPublishStatus,
} = filesSlice.actions;
export default filesSlice.reducer;

export const selectFilesState = (state: RootState) => state.files;
export const selectError = (state: RootState) => state.files.error;
const selectSelectedSection = (state: RootState) => state.files.selectedSection;

export const selectIsEmpty = createSelector(
  [selectSelectedSection, selectFilesState],
  (section, { userFiles, otherFiles }) => {
    return (
      Object.keys(userFiles[section][UPLOADED]).length === 0 &&
      Object.keys(userFiles[section][MAPPING]).length === 0 &&
      Object.keys(userFiles[section][STAGED]).length === 0 &&
      Object.keys(otherFiles[section]).length === 0
    );
  }
);
